/**
 * flex-portal is the SOLE unit converter for the target-SoC command (LC-292 / LC-291 Layer-1
 * contract). The form collects %, kW and kW/s; this module converts to the downstream units —
 * fraction 0..1, integer Watts, integer Watts/second — and an ISO-8601 `validUntil` string. The
 * BFF and everything below it never see display units.
 */
import { DateTime } from 'luxon'
import type { z } from 'zod'

import type { TargetSoCRequest } from '@/features/resource/endpoints/resources'
import type { getTargetSoCSchema } from '@/features/resource/schemas'

export type TargetSoCFormValues = {
  /** Required. Percent 0..100; converted to a fraction 0..1 on submit. */
  targetSoCPercent: number | null
  /** Optional. kW; converted to integer Watts. */
  maxSetpointChargeKw: number | null
  maxSetpointDischargeKw: number | null
  /** Optional. kW/s; converted to integer Watts/second. */
  maxRampSpeedChargeKwPerS: number | null
  maxRampSpeedDischargeKwPerS: number | null
  /** Required. Defaults to now + 10 min, user-editable; sent as an ISO-8601 string. */
  validUntil: Date | null
}

/**
 * The form values after validation has guaranteed the two required fields are present. Producing
 * this via {@link parseValidatedTargetSoCFormValues} lets {@link buildTargetSoCRequestBody} be a
 * total function (no runtime null-guard needed inside it).
 */
export type ValidatedTargetSoCFormValues = Omit<TargetSoCFormValues, 'targetSoCPercent' | 'validUntil'> & {
  targetSoCPercent: number
  validUntil: Date
}

type TypesEqual<A, B> = (<T>() => T extends A ? 1 : 2) extends <T>() => T extends B ? 1 : 2 ? true : false
type Expect<T extends true> = T

// Compile-time drift guard: the hand-written form-values type must stay identical to the Zod
// schema's input type. If either side changes without the other, this alias stops compiling.
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- type-only assertion, no runtime use
type _FormValuesMatchSchema = Expect<TypesEqual<TargetSoCFormValues, z.input<ReturnType<typeof getTargetSoCSchema>>>>

/** Minutes the `validUntil` field is pre-filled ahead of "now" (delivery-staleness guard, editable). */
export const TARGET_SOC_DEFAULT_VALID_UNTIL_MINUTES = 10

/** kW → integer W. `null` (an unset optional field) stays absent from the body. */
function kwToWatts(kw: number | null): number | undefined {
  return kw === null ? undefined : Math.round(kw * 1000)
}

/** kW/s → integer W/s. `null` (an unset optional field) stays absent from the body. */
function kwPerSecondToWattsPerSecond(kwPerSecond: number | null): number | undefined {
  return kwPerSecond === null ? undefined : Math.round(kwPerSecond * 1000)
}

/** Percent 0..100 → fraction 0..1. */
function percentToFraction(percent: number): number {
  return percent / 100
}

/**
 * Narrow raw form values to {@link ValidatedTargetSoCFormValues}, or `null` when a required field
 * is still empty. The Zod schema already blocks submit in that case, so `null` is a defensive
 * no-op path for callers rather than an expected outcome.
 */
export function parseValidatedTargetSoCFormValues(values: TargetSoCFormValues): ValidatedTargetSoCFormValues | null {
  if (values.targetSoCPercent === null || values.validUntil === null) {
    return null
  }
  return { ...values, targetSoCPercent: values.targetSoCPercent, validUntil: values.validUntil }
}

/**
 * Build the POST body from validated form values. Optional fields left empty are omitted entirely
 * (absent = battery capability / no limit, per AD-4) rather than sent as 0.
 */
export function buildTargetSoCRequestBody(values: ValidatedTargetSoCFormValues): TargetSoCRequest {
  const validUntil = DateTime.fromJSDate(values.validUntil).toUTC().toISO()
  if (validUntil === null) {
    // toISO() only returns null for an invalid DateTime; refuse to ship an empty validUntil.
    throw new Error('validUntil is not a valid date')
  }

  return {
    targetSoC: percentToFraction(values.targetSoCPercent),
    maxSetpointCharge: kwToWatts(values.maxSetpointChargeKw),
    maxSetpointDischarge: kwToWatts(values.maxSetpointDischargeKw),
    maxRampSpeedCharge: kwPerSecondToWattsPerSecond(values.maxRampSpeedChargeKwPerS),
    maxRampSpeedDischarge: kwPerSecondToWattsPerSecond(values.maxRampSpeedDischargeKwPerS),
    validUntil,
  }
}

/** The default `validUntil` value: now + {@link TARGET_SOC_DEFAULT_VALID_UNTIL_MINUTES} minutes. */
export function defaultValidUntil(): Date {
  return DateTime.local().plus({ minutes: TARGET_SOC_DEFAULT_VALID_UNTIL_MINUTES }).toJSDate()
}
